import type { CanonicalEntity } from '@/shared/api/types';

/**
 * Владение секциями опубликованной SKP-модели.
 *
 * Публикация SketchUp (`sourceKind: 'sketchup_decode'`) кладёт все исходные
 * меши плоскими детьми синтетического корня `sketchup-import:root`, а
 * человеческую группировку кухни сохраняет только в исходном пути меша
 * (`domain.sourcePath`: "ROOT / Тумба 600 / Body").  До этого файла
 * `entityModuleOwnerId` возвращал таким мешам САМ КОРЕНЬ — модульная
 * агрегация собирала всю кухню одним «модулем», и клик/драг по отдельному
 * корпусу в воркспейсе не работал.
 *
 * Секция — первые два сегмента исходного пути ("ROOT", "Тумба 600"), ровно
 * тот же договор, что у бэкенд-проекции `skp_native_module_projection`
 * (`_container_path`): контейнер обязан называть ROOT и конкретного первого
 * ребёнка.  Идентификатор секции выводится из имени контейнера, поэтому он
 * стабилен между перезагрузками и переизданиями той же раскладки.
 *
 * Меши БЕЗ контейнера (распознанная фурнитура, контекст, рыхлая геометрия) и
 * сам синтетический корень владельцем модуля НЕ становятся: сваливать всю
 * сцену в один перетаскиваемый агрегат — та же ошибка, которую чинит этот
 * файл.  Канонические поля бандла (moduleId, parentId) здесь не
 * перезаписываются — владение вычисляется на лету, сцена только читает.
 */

export const SKP_SECTION_ID_PREFIX = 'skp-section:';

type Domain = Record<string, unknown>;

function entityDomain(entity: CanonicalEntity | null | undefined): Domain {
  return (entity?.domain && typeof entity.domain === 'object' ? entity.domain : {}) as Domain;
}

function domainString(domain: Domain, key: string): string {
  const value = domain[key];
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

/**
 * Контейнер секции из исходного пути: `["ROOT", "Тумба 600"]` либо null.
 * Требования те же, что у `_container_path` бэкенда: минимум два сегмента,
 * первый — ровно "ROOT".
 */
export function skpSourceContainer(
  sourcePath: unknown,
): readonly [root: string, section: string] | null {
  if (typeof sourcePath !== 'string') return null;
  const segments = sourcePath.split('/').map((part) => part.trim()).filter(Boolean);
  if (segments.length < 2 || segments[0] !== 'ROOT') return null;
  return [segments[0], segments[1]];
}

/** Признак меша/детали опубликованной SKP-модели. */
export function isSketchupSourceEntity(entity: CanonicalEntity | null | undefined): boolean {
  return domainString(entityDomain(entity), 'sourceAuthority') === 'sketchup_decode';
}

/** Синтетический корень публикации SKP (`moduleType: imported_sketchup`). */
export function isSketchupImportRootEntity(entity: CanonicalEntity | null | undefined): boolean {
  return domainString(entityDomain(entity), 'moduleType') === 'imported_sketchup';
}

function sectionIdOfContainer(section: string): string {
  return `${SKP_SECTION_ID_PREFIX}${encodeURIComponent(section)}`;
}

/**
 * Владелец-модуль SKP-сущности: id секции, к которой меш принадлежит, либо
 * '' — меш без контейнера и синтетический корень модулем не владеются.
 * Для не-SKP сущностей всегда '' (решение остаётся за обычным договором).
 */
export function skpSectionModuleOwnerId(entity: CanonicalEntity | null | undefined): string {
  if (!entity) return '';
  const domain = entityDomain(entity);
  if (isSketchupImportRootEntity(entity)) return '';
  if (!isSketchupSourceEntity(entity)) return '';
  const container = skpSourceContainer(domain.sourcePath);
  return container ? sectionIdOfContainer(container[1]) : '';
}

/**
 * Человеческое имя секции из исходного пути меша ("Тумба 600") — для
 * подписей агрегата, когда у сущности нет своего имени/blockLabel.
 */
export function skpSectionLabelOf(entity: CanonicalEntity | null | undefined): string {
  if (!entity) return '';
  if (!isSketchupSourceEntity(entity)) return '';
  const container = skpSourceContainer(entityDomain(entity).sourcePath);
  return container ? container[1] : '';
}

/**
 * Синтетический id владельца секции (`skp-section:<имя>`) — им владеют меши,
 * но среди сущностей каноны его НЕТ.  Выделение сцены резолвится в такой id,
 * и каждый потребитель, ищущий выбранный id в entityById, обязан знать, что
 * здесь живёт агрегат без сущности, а не потерянная ссылка.
 */
export function isSkpSectionOwnerId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith(SKP_SECTION_ID_PREFIX);
}

/**
 * Чистое решение обводки выделения: какие id выборки — секции SKP без
 * канонической сущности, но с живыми членами в сцене.  Потребитель рисует по
 * ним агрегатную рамку; без этого шага клик по корпусу SKP писал бы в стор
 * валидный id, который не умеет показать ни один потребитель выделения —
 * в точности прод-симптом «клик не выделяет».
 */
export function skpSectionOutlineIdsWithoutEntity(
  ids: readonly string[],
  entityById: ReadonlyMap<string, unknown>,
  hasRuntimeMembers: (sectionId: string) => boolean,
): string[] {
  return ids.filter((id) => !entityById.has(id) && isSkpSectionOwnerId(id) && hasRuntimeMembers(id));
}
